import { Component, h, Host, Prop, State, Watch, Fragment, Element } from '@stencil/core';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

// Anmerkung, wenn das in dem Entry ist, ist das dann garkeine cms-component?
export interface RatingResponse {
  id: string;
  positiveRatings: string;
  rated: string;
  totalRatings: string;
}

interface UiCmsRatingBody {
  itemId: string;
  countItemLabel: string;
  feedbackItemLabel: string;
  requestUrl: string;
  requestUrlPositive: string;
  requestUrlNegative: string;
  apiKey: string;
  apiVersion: string;
  targetId: string;
}

@Component({
  tag: 'ui-cms-rating',
  styleUrl: 'ui-cms-rating.scss',
  shadow: true,
})
export class UiCmsRating {
  @Element() el: HTMLUiCmsRatingElement;

  @State() positiveRatings = 0;

  @State() totalRatings = 0;

  @State() rated = false;

  @State() error = false;

  @State() ratingButtonWidth = 0;

  @Prop() additionalTrackingContent: string;

  @Prop() withFeedbackModal = false;

  @Prop() requestUrlFeedback: string;

  @Prop() faq?: string;

  @Prop() url?: string;

  @Prop({ mutable: true }) body: UiCmsRatingBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) i18n = {
    error:
      'Aufgrund technischer Probleme ist eine Bewertung aktuell nicht möglich. Bitte versuchen Sie es später erneut.',
    yes: 'Ja',
    no: 'Nein',
    from: 'von',
    helpful: 'hilfreich',
    ratingsSingular: 'Person fand',
    ratingsPlural: 'Personen fanden',
    thanksForFeedback: 'Vielen Dank für Ihr Feedback!',
    wasFeedbackHelpful: 'War $ENTRY hilfreich für Sie?',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  uuid: string;

  private upButtonElement: HTMLUiButtonElement;

  private downButtonElement: HTMLUiButtonElement;

  private feedbackElement: HTMLUiCmsFaqFeedbackElement;

  async componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    this.uuid = Cookies.get('uuid');

    if (!this.uuid) {
      this.uuid = `RATING-${uuidv4()}`;
      Cookies.set('uuid', this.uuid, { expires: 365 * 10 });
    }

    await fetch(
      `${this.body.requestUrl}?id=${this.body.itemId}&userid=${this.uuid}&targetid=${this.body.targetId}&api-version=${this.body.apiVersion}`,
      {
        headers: {
          'x-api-key': this.body.apiKey,
        },
      }
    )
      .then((response) => response.json())
      .then((data: RatingResponse) => {
        this.updateState(data);
      })
      .catch((error) => {
        this.error = true;
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  componentDidLoad() {
    if (!this.rated) {
      this.getWidthOfWiderButton();
    }
  }

  getWidthOfWiderButton(): void {
    if (this.upButtonElement && this.downButtonElement) {
      let upButtonWidth = 0;
      let downButtonWidth = 0;

      const upButtonPromise = (this.upButtonElement as any).updateComplete.then(() => {
        upButtonWidth = this.upButtonElement.offsetWidth;
      });

      const downButtonPromise = (this.downButtonElement as any).updateComplete.then(() => {
        downButtonWidth = this.downButtonElement.offsetWidth;
      });

      Promise.all([upButtonPromise, downButtonPromise]).then(() => {
        this.ratingButtonWidth = Math.max(upButtonWidth, downButtonWidth);
      });
    }
  }

  trackAction(isPositive: boolean) {
    window.ui?.services?.tracking?.track({
      eventInfo: {
        eventName: 'Rating Tracking',
        eventType: 'Action',
        eventCaller: this.el,
      },
      category: {
        area: 'Content',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: isPositive ? 'Ja' : 'Nein',
        virtualTarget: this.additionalTrackingContent,
      },
    });
  }

  onLikeClick() {
    if (this.withFeedbackModal) {
      this.openFeedbackModal(true);
    }
    this.sendFeedback(true);
    this.trackAction(true);
  }

  onDislikeClick() {
    if (this.withFeedbackModal) {
      this.openFeedbackModal(false);
    }
    this.sendFeedback(false);
    this.trackAction(false);
  }

  updateState(data: RatingResponse) {
    this.positiveRatings = parseInt(data.positiveRatings, 10);
    this.totalRatings = parseInt(data.totalRatings, 10);
    this.rated = data.rated === 'true';
  }

  sendFeedback(isPositive: boolean) {
    const feedbackUrl = isPositive ? this.body.requestUrlPositive : this.body.requestUrlNegative;
    fetch(
      `${feedbackUrl}?id=${this.body.itemId}&userid=${this.uuid}&targetid=${this.body.targetId}&api-version=${this.body.apiVersion}`,
      {
        headers: {
          'x-api-key': this.body.apiKey,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        this.updateState(data);
      })
      .catch((error) => {
        this.error = true;
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  renderHeadlineText() {
    return this.rated
      ? this.i18n.thanksForFeedback
      : this.i18n.wasFeedbackHelpful.replace('$ENTRY', this.body.feedbackItemLabel);
  }

  getSigularOrPlural() {
    return this.totalRatings === 1 ? this.i18n.ratingsSingular : this.i18n.ratingsPlural;
  }

  openFeedbackModal(isPositive: boolean): void {
    this.feedbackElement.showFaqFeebackModal(isPositive);
  }

  render() {
    return (
      <Host>
        {this.withFeedbackModal && (
          <ui-cms-faq-feedback
            body={{
              uuid: this.uuid,
              url: this.url,
              faq: this.faq,
              requestUrlFeedback: this.requestUrlFeedback,
              apiKey: this.body.apiKey,
              apiVersion: this.body.apiVersion,
            }}
            // todo add i18
            ref={(el) => {
              this.feedbackElement = el;
            }}
          ></ui-cms-faq-feedback>
        )}
        <p class="sd-paragraph sd-paragraph--size-sm">
          {`${this.positiveRatings} ${this.i18n.from}`}
          {` ${this.totalRatings} ${this.getSigularOrPlural()} ${this.body.countItemLabel} ${this.i18n.helpful}`}.
        </p>
        {this.error ? (
          <p class="sd-paragraph error">{this.i18n.error}</p>
        ) : (
          <h4 class="sd-headline--size-base">{this.renderHeadlineText()}</h4>
        )}
        {!this.rated && (
          <Fragment>
            <sd-button
              ref={(el) => {
                this.upButtonElement = el;
              }}
              // this.ratingButtonWidth saves num without comma f.e. 94 instead of 94.345
              style={this.ratingButtonWidth > 0 && { width: `${this.ratingButtonWidth + 1}px` }}
              onClick={() => this.onLikeClick()}
            >
              {this.i18n.yes}
              <sd-icon library={window.ui.cmsState.iconLibrary} name="system/thumbs-up" slot="icon-left"></sd-icon>
            </sd-button>
            <sd-button
              ref={(el) => {
                this.downButtonElement = el;
              }}
              // this.ratingButtonWidth saves num without comma f.e. 94 instead of 94.345
              style={this.ratingButtonWidth > 0 && { width: `${this.ratingButtonWidth + 1}px` }}
              onClick={() => this.onDislikeClick()}
            >
              {this.i18n.no}
              <sd-icon library={window.ui.cmsState.iconLibrary} name="system/thumbs-down" slot="icon-left"></sd-icon>
            </sd-button>
          </Fragment>
        )}
      </Host>
    );
  }
}
